import React, { useContext } from 'react';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import _isFunction from 'lodash/isFunction';
import { intlMessage } from 'constants/propTypes';
import { ICONS } from 'constants/icons';

import When from 'components/When';
import { bem } from 'helpers/class-name';
import { DropdownContext } from 'features/App/Contexts';
import appMessages from 'features/App/App.messages';

import './DropdownMenu.scss';

const { block, element } = bem('dropdown-menu2');

function DropdownMenu(props) {
  const { getItemId, menuId } = useContext(DropdownContext);
  const intl = useIntl();

  // The isMenuOnly prop is used for custom dropdowns that require additional customization, e.g., titles in the menu
  // or layout elements that can't be selected. Most dropdowns will include DropdownItem or DropdownItemCheckbox elements and
  // `isMenuOnly` should be set to false.

  const {
    children,
    className,
    isMenuOnly = false,
    renderPropData,
    fallbackSelectedItemRef,
    onSelect,
    showCloseButton = false,
    stretchToFit,
  } = props;
  const closeAriaLabel = intl.formatMessage(appMessages.closeMenuAriaLabel);

  let renderPropChildren = children;
  if (_isFunction(children)) {
    renderPropChildren = children(renderPropData);
  }
  renderPropChildren = React.Children.toArray(renderPropChildren);

  return (
    <div id={menuId} className={block(className, { stretchToFit })}>
      <When predicate={showCloseButton}>
        <button className={element('close-btn')} type="button" onClick={onSelect}>
          <Isvg src={ICONS.cross} aria-label={closeAriaLabel} />
        </button>
      </When>
      {renderPropChildren.map(child =>
        isMenuOnly
          ? React.cloneElement(child)
          : React.cloneElement(child, { onSelect, fallbackSelectedItemRef, id: getItemId() })
      )}
    </div>
  );
}

DropdownMenu.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
  isMenuOnly: PropTypes.bool,
  renderPropData: PropTypes.arrayOf(
    PropTypes.shape({
      option: PropTypes.oneOfType([
        PropTypes.string,
        intlMessage,
        PropTypes.shape({
          label: PropTypes.oneOfType([PropTypes.string, intlMessage]),
          isFilterable: PropTypes.bool,
          isAllSelectedOption: PropTypes.bool,
        }),
      ]),
      ref: PropTypes.shape({ current: PropTypes.oneOfType([PropTypes.node, PropTypes.object]) }),
      isHighlighted: PropTypes.bool,
    })
  ),
  fallbackSelectedItemRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
  }),
  showCloseButton: PropTypes.bool,
  stretchToFit: PropTypes.bool,
  onSelect: PropTypes.func,
};

export default DropdownMenu;
